import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { motion, type Variants } from "framer-motion";
import { breadcrumbSchema, personSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export default function WhatImBuilding() {
  const SEO_TITLE = "What I'm Building | Shariq Refai, MD, MBA";
  const SEO_DESCRIPTION =
    "A founder narrative on the Shrink Network: why I started shrinkMD, why I built Shrinkopedia, shrinQ, and the educational properties, and what comes next.";

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "What I'm Building", path: "/what-im-building" },
  ];

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/what-im-building"
        image="/og/what-im-building.jpg"
        keywords="Shariq Refai founder vision, The Shrink Network, shrinkMD, Shrinkopedia, shrinQ, AnxietyResearch, PsychiatryRx, mental health ecosystem"
        schema={[
          personSchema,
          webPageSchema({
            path: "/what-im-building",
            title: SEO_TITLE,
            description: SEO_DESCRIPTION,
            breadcrumbs,
          }),
          breadcrumbSchema(breadcrumbs),
        ]}
      />

      {/* Intro */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "What I'm Building" },
            ]}
          />
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="flex flex-col gap-5"
          >
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              Founder Narrative
            </span>
            <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
              What I'm building at The Shrink Network
            </h1>
            <p className="text-lg md:text-xl text-secondary font-light leading-relaxed">
              A coordinated ecosystem of mental health properties, written one
              site at a time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-li:font-light prose-li:text-secondary prose-strong:text-primary prose-strong:font-medium prose-a:text-accent hover:prose-a:text-primary">
          <h2>The short version</h2>
          <p>I'm building the most coherent collection of clinically grounded mental health resources I can build. Clinical care in one place. Education in another. Tools and programs in their own places. A founder site where the writing lives. All separated for clarity, all connected for the reader, all reviewed by a board-certified psychiatrist (me).</p>

          <h2>Why shrinkMD</h2>
          <p>shrinkMD is the foundation. Without a clinical practice, the rest of the work would be theoretical. shrinkMD treats individual patients with psychiatric care across multiple states via telepsychiatry. It's the place I send people who need actual care.</p>
          <p>I built it because access to a psychiatrist in this country is still measured in months for too many people. Telepsychiatry isn't a workaround. For most adult outpatient psychiatric conditions, it's clinically equivalent to in-person care, and it makes the math work for patients who couldn't otherwise get to a clinic.</p>

          <h2>Why Shrinkopedia</h2>
          <p>Because the explanations on the internet are either too thin or too academic and almost none of them are written by clinicians. Shrinkopedia is the encyclopedia. It explains what conditions are, how they're diagnosed, and what the treatments do, in plain language, with every claim sourced.</p>
          <p>I write or review every entry. The site doesn't run ads. It doesn't take affiliate commissions. It's there because the reader at 2 a.m. searching "what is generalized anxiety disorder" deserves a real answer.</p>

          <h2>Why AnxietyResource and DepressionResource</h2>
          <p>Because anxiety and depression aren't a single experience and don't deserve a single page. Each gets its own dedicated site with practical, condition-specific guidance and resources. Where Shrinkopedia explains, AnxietyResource and DepressionResource translate the explanation into "here's what to actually do about it day to day."</p>

          <h2>Why PsychiatryRx</h2>
          <p>Because a patient holding a prescription bottle wants plain language about what's in it and what to expect, and the official labeling reads like it was written for pharmacists. PsychiatryRx is the medication reference written for the person who's about to take the pill.</p>

          <h2>Why AnxietyResearch</h2>
          <p>Because the research literature on anxiety is rich and almost none of it is translated for general readers. AnxietyResearch turns the studies, the meta-analyses, and the public datasets into plain-English summaries that journalists, clinicians, and curious readers can use.</p>

          <h2>Why shrinQ</h2>
          <p>Because seeing the pattern isn't the same as working with it. shrinQ is a structured educational system for overthinking, rumination, and the anxiety loops that don't yield to insight alone. It teaches a four-move framework (Pattern, Pause, Reframe, Act) that I use clinically, turned into something a reader can work through on their own.</p>
          <p>It's not therapy. It's not a substitute for clinical care. It's the structure I'd hand a patient between sessions.</p>

          <h2>Why Unstuck and QuitScrolling</h2>
          <p>Because there's a moment between "I'm overwhelmed" and "I need a clinician" where a small, well-designed tool can be the difference. Unstuck is the wellness app for the daily moment when you need to interrupt a pattern. QuitScrolling addresses the attention-pull that turns into anxiety for a lot of modern adults.</p>

          <h2>Why ShrinkDaily</h2>
          <p>Because understanding your mind works better as a daily habit than as a one-time read. ShrinkDaily publishes one short, evidence-based mental health concept at a time, written to be useful on its own and to route you deeper into the network when a topic needs more room.</p>

          <h2>Why the books</h2>
          <p>Because some of the work belongs on paper. The first book, Your Mind Is Full of Sh*t, names the patterns I've watched run people's lives for fifteen years and gives the science underneath them. The companion Workbook is the do-the-work piece. The third book, The Havoc in Your Head, focuses on the reset patterns for panic, overthinking, and the thoughts that won't let go.</p>

          <h2>Why shrinkiatry</h2>
          <p>Because the profession itself deserves an honest inside view. shrinkiatry is the site for what psychiatry is actually like to practice, what the training is, what the work feels like, and what the field gets right and wrong about itself.</p>

          <h2>What comes next</h2>
          <p>A few things are in active build:</p>
          <ul>
            <li>More deep-expansion entries on Shrinkopedia, especially in the conditions and medications categories.</li>
            <li>More Evidence Maps on AnxietyResearch.</li>
            <li>The shrinQ launch (Stripe checkout opens shortly).</li>
            <li>More books. The next manuscript is the high-performer focused one.</li>
            <li>More speaking and media. The clinical and educational arguments need to be made in the rooms where decisions get made.</li>
          </ul>

          <h2>Why all of this fits together</h2>
          <p><a href={withNetworkUtm("https://shrinknetwork.com/", "what_im_building_page")} rel="noopener" className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors">The Shrink Network</a>'s connective tissue is consistency. Same medical reviewer. Same evidence methodology. Same conflict-of-interest disclosures. Same crisis routing. The same person standing behind all of it, with verifiable credentials and a willingness to put a name on every claim.</p>
          <p>That consistency is the moat. A competitor can copy a single site. They can't easily copy a coordinated ecosystem with one psychiatrist at the center who actually practices, writes, builds, and reviews.</p>
        </div>
      </article>

      {/* Continue */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-muted/30 border-t border-border/50">
        <div className="container mx-auto max-w-3xl">
          <NetworkRouter
            eyebrow="Continue"
            cards={[
              { label: "See the full project list", href: "/projects" },
              { label: "Read the founder narrative", href: "/founder-of-the-shrink-network" },
              { label: "Read the books", href: "/books" },
              { label: "See The Shrink Network", href: withNetworkUtm("https://shrinknetwork.com/", "what_im_building_page") },
            ]}
          />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/projects", label: "Projects founded, built, and reviewed", why: "The current inventory of everything shipping." },
          { href: "/founder-of-the-shrink-network", label: "Founder of The Shrink Network", why: "The bigger picture and the architecture." },
          { href: "/about", label: "About Shariq Refai, MD, MBA", why: "The person doing the building." },
          { href: "/books", label: "Books by Shariq Refai", why: "The three books in progress." },
        ]}
      />
    </SiteLayout>
  );
}
